import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import AuthService from '@omni/kit/services/AuthService';
import { IRootProps } from '@omni/kit/contexts/types';
import { accessTokenSelector } from '../shared/redux/selectors';
import { getTokenUrl } from '@omni/kit/services/AuthService/utilities';
import { setAccessToken } from '../shared/redux/actions/SystemActions';
import { useSourceContext } from '@omni/kit/contexts/SourceContext';

const debug = require('debug')('omni:messaging:context:ChatAuthContext');

interface ChatAuthContextProps {
  isAuthenticated: boolean;
  isReady: boolean;
}

export const ChatAuthContext = createContext<ChatAuthContextProps>({
  isAuthenticated: Boolean(AuthService.accessToken),
  isReady: false,
});

interface ProviderProps {
  children?: React.ReactNode | Array<React.ReactNode>;
  props?: IRootProps;
}

/**
 * This context provider is meant to sync the AuthService's state with the Redux state for Chat
 * The idea being that each module that needs to _hook_ into the auth state,
 * should wrap the app tree in this provider.
 * This is meant to be used once in the app tree.
 *
 * TODO: consolidate with packages/kit/context/AuthContext.tsx
 */
export const ChatAuthContextProvider = ({
  children,
  props,
}: ProviderProps): JSX.Element | null => {
  const source = useSourceContext();

  const accessToken = useSelector(accessTokenSelector);

  const [isReady, setIsReady] = useState(false);
  const isAuthenticated = useMemo(() => Boolean(accessToken), [accessToken]);

  const appKey = props?.appKey;

  useEffect(() => {
    const serviceCleanup = AuthService.initialize();

    const setup = async (appKey: string) => {
      let tokenUrl = props?.tokenUrl; // passed as screen prop on mobile

      // when user switches apps on the web, we need to resolve the tokenUrl,
      // otherwise it will attempt to refresh token with the tokenUrl
      // from local storage, which may be the token url for a different app
      if (!tokenUrl) {
        try {
          tokenUrl = await getTokenUrl(appKey, props?.authProviderId);
        } catch {}
      }

      AuthService.loadCredentials({ ...props, source, tokenUrl })
        .catch((e) => {
          debug(`Unable to initialize auth context: ${e}`);
        })
        .finally(() => {
          setIsReady(true);
        });
    };

    if (appKey) {
      setup(appKey);
    }

    return () => {
      serviceCleanup();
    };
  }, [appKey]);

  const dispatch = useDispatch();

  const removeListener = useRef<(() => void) | null>(null);
  useEffect(() => {
    removeListener.current?.();

    const _signinSuccess = async (token: string) => {
      dispatch(setAccessToken(token));
    };

    const _signinFailure = async () => {
      // Auth failure is permanent, so we must prompt the user to login.
      // We do this by clearing the access token which allows the AuthNavigator to appear.
      dispatch(setAccessToken(''));
    };

    removeListener.current = AuthService.addListener(
      'AuthorizationChanged',
      ({ token }) => {
        if (token && token !== accessToken) {
          _signinSuccess(token);
        } else if (!token) {
          _signinFailure();
        }
      }
    );

    // ref: https://reactjs.org/blog/2020/08/10/react-v17-rc.html#potential-issues
    const cleanup = removeListener.current;

    return () => {
      cleanup?.();
    };
  }, [accessToken, dispatch]);

  return (
    <ChatAuthContext.Provider value={{ isAuthenticated, isReady }}>
      {children}
    </ChatAuthContext.Provider>
  );
};

export const useChatAuth = (): ChatAuthContextProps => {
  return useContext<ChatAuthContextProps>(ChatAuthContext);
};
